import type { MatchGridSavedColumns } from '../../schema/spark/match-grid-column-config-api-schema';
import type { MatchGridColumnConfigItem } from '../types/match-grid.types';

const LABEL_KEY = 'label';

const idOf = (item: MatchGridColumnConfigItem) =>
  Object.keys(item).find(key => key !== LABEL_KEY);

const isShown = (item: MatchGridColumnConfigItem) => {
  const id = idOf(item);

  return id !== undefined && item[id] === true;
};

/**
 * MatchGridColumnConfigUtil - I carry a column configuration between the two shapes it lives in:
 * the endpoint's pair of ordered lists - the columns that show and the columns that do not - and
 * the grid's own single list of every configurable column with its label and whether it shows.
 */
export const MatchGridColumnConfigUtil = {
  columnIdOf({ item }: { item: MatchGridColumnConfigItem }) {
    return idOf(item);
  },

  /**
   * Whether this is the configuration every reader starts with - the same columns, in the same
   * order, shown and hidden the same way. **Order counts**, so a reader who has only dragged
   * something is not told they are back where they began.
   *
   * `defaults` is passed in like every other set of columns here, so this stays a comparison of
   * two lists rather than a util that knows which columns the grid happens to have.
   */
  isDefault({
    columns,
    defaults,
  }: {
    columns: MatchGridColumnConfigItem[];
    defaults: MatchGridColumnConfigItem[];
  }) {
    const asKeys = (items: MatchGridColumnConfigItem[]) =>
      items
        .map(item => {
          const id = MatchGridColumnConfigUtil.columnIdOf({ item });

          return id ? `${id}:${String(item[id])}` : '';
        })
        .join(',');

    return asKeys(columns) === asKeys(defaults);
  },

  /**
   * **Every configurable column is named, in one list or the other.** A column left out of both is
   * how `toForm` knows one has been added since - so dropping the hidden ones here would make
   * every save look like it was written against an older set of columns.
   */
  toApiPayload({ columns }: { columns: MatchGridColumnConfigItem[] }) {
    const disabledColumns: string[] = [];
    const enabledColumns: string[] = [];

    columns.forEach(item => {
      const columnName = MatchGridColumnConfigUtil.columnIdOf({ item });

      if (columnName === undefined) return;

      if (item[columnName] === true) {
        enabledColumns.push(columnName);
      } else {
        disabledColumns.push(columnName);
      }
    });

    return { disabledColumns, enabledColumns } satisfies MatchGridSavedColumns;
  },

  /**
   * A saved configuration brought up to date with the columns there are now.
   *
   * **A column it names in neither list shipped after it was written**, so it joins the end of the
   * list its own default puts it in - on for a column that ships shown, off for one that ships
   * hidden. Without this a column added later would be indistinguishable from one the reader had
   * turned off, and would arrive hidden for everyone who had already saved.
   */
  withNewColumns({
    columns,
    saved,
  }: {
    columns: MatchGridColumnConfigItem[];
    saved: MatchGridSavedColumns;
  }) {
    const named = new Set([...saved.enabledColumns, ...saved.disabledColumns]);
    const disabledColumns = [...saved.disabledColumns];
    const enabledColumns = [...saved.enabledColumns];

    columns.forEach(item => {
      const id = idOf(item);

      if (id === undefined || named.has(id)) return;

      if (isShown(item)) {
        enabledColumns.push(id);
      } else {
        disabledColumns.push(id);
      }
    });

    return { disabledColumns, enabledColumns } satisfies MatchGridSavedColumns;
  },

  /**
   * The saved lists back into the grid's own: the columns that show first, each in the order it
   * was left in, then the ones that do not.
   */
  toFormPayload({
    columns,
    saved,
  }: {
    columns: MatchGridColumnConfigItem[];
    saved: MatchGridSavedColumns;
  }) {
    /* Before filling any gaps, so a reader with nothing saved gets the defaults exactly as written. */
    if (
      saved.enabledColumns.length === 0 &&
      saved.disabledColumns.length === 0
    ) {
      return columns;
    }

    const { disabledColumns, enabledColumns } =
      MatchGridColumnConfigUtil.withNewColumns({ columns, saved });

    const itemsById = new Map(
      columns.flatMap(item => {
        const id = MatchGridColumnConfigUtil.columnIdOf({ item });

        return id === undefined ? [] : [[id, item] as const];
      })
    );

    const taken = new Set<string>();

    /* A list can name a column twice, or one the grid no longer has: both are dropped here. */
    const asItems = (columnNames: string[], shown: boolean) =>
      columnNames.flatMap(columnName => {
        const item = itemsById.get(columnName);

        if (!item || taken.has(columnName)) return [];

        taken.add(columnName);

        return [{ label: item.label, [columnName]: shown }];
      });

    return [
      ...asItems(enabledColumns, true),
      ...asItems(disabledColumns, false),
    ];
  },
};
